import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ChevronDown } from "lucide-react";
import { Page } from "@/components/Page";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, RevealStagger, StaggerItem } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { SERVICES } from "@/data/site";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const [openStep, setOpenStep] = useState(0);

  if (!service) return <Navigate to="/services" replace />;

  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
  const Icon = service.icon;

  return (
    <Page>
      <SEO title={service.name} description={service.short} />

      <PageHero
        eyebrow="Service"
        title={service.name}
        intro={service.short}
        image={service.image}
      >
        <Button to="/services" variant="outlineLight" size="md">
          <ArrowLeft className="h-4 w-4" /> All Services
        </Button>
      </PageHero>

      {/* Overview */}
      <section className="py-24 lg:py-28">
        <div className="ssa-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <div className="lg:col-span-7">
              <SectionHeading eyebrow="Service Overview" title={`Excellence in ${service.name.toLowerCase()}`} />
              <p className="mt-7 text-[15px] leading-relaxed text-secondary-ssa">{service.overview}</p>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {service.highlights.map((h) => (
                  <span key={h} className="inline-flex items-center gap-2 rounded-full border border-[color:var(--ssa-border-subtle)] bg-white px-4 py-2 text-sm text-navy">
                    <Check className="h-4 w-4 text-turquoise" /> {h}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <Reveal>
                <div className="rounded-2xl overflow-hidden shadow-[0_30px_70px_rgba(8,28,58,0.2)]">
                  <img src={service.image} alt={service.name} className="w-full h-[360px] object-cover" />
                </div>
                <div className="mt-5 rounded-2xl bg-navy text-white p-6 flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-turquoise shrink-0">
                    <Icon className="h-6 w-6" />
                  </div>
                  <p className="text-sm text-white/80">Part of an integrated, door-to-door logistics offering managed under one accountable partner.</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 lg:py-28 ocean-sheen">
        <div className="ssa-container">
          <SectionHeading eyebrow="Operational Process" title="A disciplined, transparent workflow" className="mb-12" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Desktop stepper */}
            <div className="hidden lg:grid lg:col-span-12 grid-cols-4 gap-6">
              {service.process.map((p, i) => (
                <Reveal key={p.step} delay={i * 0.06}>
                  <div className="relative h-full rounded-2xl bg-white border border-[color:var(--ssa-border-subtle)] p-7">
                    <div className="font-mono-ssa text-sm text-turquoise">0{i + 1}</div>
                    <h3 className="mt-3 text-lg text-navy">{p.step}</h3>
                    <p className="mt-2 text-sm text-secondary-ssa leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            {/* Mobile accordion */}
            <div className="lg:hidden col-span-1 space-y-3">
              {service.process.map((p, i) => (
                <div key={p.step} className="rounded-2xl bg-white border border-[color:var(--ssa-border-subtle)] overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenStep(openStep === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-3 p-5 text-left"
                  >
                    <span className="flex items-center gap-3">
                      <span className="font-mono-ssa text-sm text-turquoise">0{i + 1}</span>
                      <span className="text-base text-navy font-medium">{p.step}</span>
                    </span>
                    <ChevronDown className={`h-5 w-5 text-navy transition-transform ${openStep === i ? "rotate-180" : ""}`} />
                  </button>
                  {openStep === i && <p className="px-5 pb-5 text-sm text-secondary-ssa leading-relaxed">{p.text}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Applications + Benefits */}
      <section className="py-24 lg:py-28">
        <div className="ssa-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <div className="lg:col-span-4">
              <SectionHeading eyebrow="Industry Applications" title="Where this service applies" />
              <div className="mt-7 flex flex-wrap gap-2.5">
                {service.applications.map((a) => (
                  <span key={a} className="rounded-full bg-navy text-white px-4 py-2 text-sm">{a}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-8">
              <SectionHeading eyebrow="Strategic Benefits" title="Value delivered to your operation" />
              <RevealStagger className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-5">
                {service.benefits.map((b) => (
                  <StaggerItem key={b.title}>
                    <div className="h-full rounded-2xl border border-[color:var(--ssa-border-subtle)] bg-white p-6">
                      <div className="h-8 w-8 rounded-lg bg-[rgba(90,168,201,0.14)] flex items-center justify-center">
                        <Check className="h-4 w-4 text-royal" />
                      </div>
                      <h3 className="mt-4 text-base text-navy">{b.title}</h3>
                      <p className="mt-2 text-sm text-secondary-ssa leading-relaxed">{b.text}</p>
                    </div>
                  </StaggerItem>
                ))}
              </RevealStagger>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-24 lg:pb-32">
        <div className="ssa-container">
          <div className="flex items-end justify-between mb-10">
            <h2 className="text-navy text-2xl sm:text-3xl">Related Services</h2>
            <Button to="/services" variant="ghost" size="md">View all <ArrowRight className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/services/${r.slug}`}
                className="group rounded-2xl border border-[color:var(--ssa-border-subtle)] bg-white p-7 transition-shadow duration-300 hover:shadow-[0_22px_55px_rgba(8,28,58,0.14)]"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-turquoise">
                  <r.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-lg text-navy">{r.name}</h3>
                <p className="mt-2 text-sm text-secondary-ssa leading-relaxed">{r.short}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-royal">
                  Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Page>
  );
};

export default ServiceDetail;
